import React, { useEffect, useMemo, useState } from 'react';
import {
  FaCheckCircle,
  FaClipboardCheck,
  FaDownload,
  FaFileAlt,
  FaGraduationCap,
  FaHistory,
  FaSave,
  FaTrash,
  FaUserGraduate,
} from 'react-icons/fa';
import StudentManagementService from '../services/StudentManagementService';
import {
  EVALUATION_STATUS_OPTIONS,
  PEDAGOGICAL_AXES,
  PEDAGOGICAL_EVALUATION_SECTIONS,
  WEEK_DAYS,
  createEvaluationData,
  createPlanningData,
  createReferralData,
} from './studentPedagogicalModels';
import { downloadStudentPedagogicalPdf } from './studentPedagogicalPdf';
import './PedagogicalEvaluationPanel.css';

const TYPE_OPTIONS = [
  {
    key: 'PLANNING',
    label: 'Planejamento Pedagógico',
    shortLabel: 'Planejamento',
    icon: FaFileAlt,
    description: 'Planejamento semanal e detalhamento das atividades de segunda a sexta-feira.',
  },
  {
    key: 'SPECIAL_EDUCATION_REFERRAL',
    label: 'Encaminhamento Educação Especial',
    shortLabel: 'Encaminhamento',
    icon: FaUserGraduate,
    description: 'Formulário de encaminhamento para os Serviços de Educação Especial.',
  },
  {
    key: 'PEDAGOGICAL_EVALUATION',
    label: 'Planilha de Avaliação Pedagógica',
    shortLabel: 'Avaliação',
    icon: FaClipboardCheck,
    description: 'Registro das condições de aprendizagem e tipos de ajuda utilizados.',
  },
];

const ELIGIBILITY_OPTIONS = [
  ['hearing', 'Deficiência Auditiva'],
  ['visual', 'Deficiência Visual'],
  ['physical', 'Deficiência Física'],
  ['intellectual', 'Deficiência Intelectual'],
  ['multiple', 'Deficiência Múltipla'],
  ['autism', 'Transtorno Global do Desenvolvimento / Transtorno do Espectro do Autismo'],
  ['giftedness', 'Altas Habilidades / Superdotação'],
  ['specialNeeds', 'Necessidades Educacionais Especiais'],
];

const clone = (value) => JSON.parse(JSON.stringify(value));

const mergeDeep = (base, patch) => {
  if (Array.isArray(base)) return Array.isArray(patch) ? patch : base;
  if (!base || typeof base !== 'object') return patch === undefined ? base : patch;
  const result = { ...base };
  Object.entries(patch && typeof patch === 'object' ? patch : {}).forEach(([key, value]) => {
    if (
      value &&
      typeof value === 'object' &&
      !Array.isArray(value) &&
      base[key] &&
      typeof base[key] === 'object' &&
      !Array.isArray(base[key])
    ) {
      result[key] = mergeDeep(base[key], value);
    } else if (value !== undefined) {
      result[key] = value;
    }
  });
  return result;
};

const createData = (type, responsibleName) => {
  if (type === 'PLANNING') return createPlanningData(responsibleName);
  if (type === 'SPECIAL_EDUCATION_REFERRAL') return createReferralData(responsibleName);
  return createEvaluationData(responsibleName);
};

const normalizeLoadedData = (type, value, responsibleName) =>
  mergeDeep(createData(type, responsibleName), value || {});

const formatDateTime = (value) => {
  if (!value) return '';
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? '' : date.toLocaleString('pt-BR');
};

const studentAge = (birthDate) => {
  if (!birthDate) return '';
  const birth = new Date(`${String(birthDate).slice(0, 10)}T12:00:00`);
  if (Number.isNaN(birth.getTime())) return '';
  const today = new Date();
  let years = today.getFullYear() - birth.getFullYear();
  const birthday = new Date(today.getFullYear(), birth.getMonth(), birth.getDate());
  if (today < birthday) years -= 1;
  return years >= 0 ? years : '';
};

function Field({ label, children, wide = false, hint = '' }) {
  return (
    <label className={`ped-eval__field ${wide ? 'ped-eval__field--wide' : ''}`}>
      <span>{label}</span>
      {children}
      {hint ? <small>{hint}</small> : null}
    </label>
  );
}

function TextInput({ label, value, onChange, type = 'text', wide = false, ...props }) {
  return (
    <Field label={label} wide={wide}>
      <input type={type} value={value ?? ''} onChange={(event) => onChange(event.target.value)} {...props} />
    </Field>
  );
}

function TextArea({ label, value, onChange, rows = 3, wide = true, ...props }) {
  return (
    <Field label={label} wide={wide}>
      <textarea rows={rows} value={value ?? ''} onChange={(event) => onChange(event.target.value)} {...props} />
    </Field>
  );
}

function SelectField({ label, value, onChange, options, wide = false, placeholder = 'Selecione' }) {
  return (
    <Field label={label} wide={wide}>
      <select value={value ?? ''} onChange={(event) => onChange(event.target.value)}>
        <option value="">{placeholder}</option>
        {options.map((option) => {
          const item = Array.isArray(option) ? { value: option[0], label: option[1] } : option;
          return <option key={String(item.value)} value={item.value}>{item.label}</option>;
        })}
      </select>
    </Field>
  );
}

function IdentityCard({ student, klass }) {
  if (!student) return null;
  const age = studentAge(student.birthDate);
  return (
    <div className="ped-eval__identity">
      <span className="ped-eval__identity-avatar">
        {student.photoUrl ? <img src={student.photoUrl} alt="" /> : <FaUserGraduate />}
      </span>
      <div>
        <strong>{student.fullName || student.socialName || 'Aluno'}</strong>
        <div>
          <span>Matrícula: <b>{student.registrationNumber || '—'}</b></span>
          <span>RA: <b>{student.ra || '—'}</b></span>
          <span>Turma: <b>{klass?.name || '—'}</b></span>
          <span>Período: <b>{klass?.period || '—'}</b></span>
          <span>Nascimento: <b>{student.birthDate ? String(student.birthDate).split('-').reverse().join('/') : '—'}</b></span>
          <span>Idade: <b>{age === '' ? '—' : `${age} ano(s)`}</b></span>
        </div>
      </div>
    </div>
  );
}

function PlanningActivityBlock({ title, value, onChange }) {
  const update = (field, fieldValue) => onChange({ ...(value || {}), [field]: fieldValue });
  return (
    <div className="ped-eval__subcard">
      <h5>{title}</h5>
      <div className="ped-eval__grid">
        <TextInput label="Espaço" value={value?.space} onChange={(v) => update('space', v)} />
        <TextInput label="Área de conhecimento" value={value?.knowledgeArea} onChange={(v) => update('knowledgeArea', v)} />
        <TextInput label="Eixo" value={value?.axis} onChange={(v) => update('axis', v)} wide />
        <TextArea label="Desenvolvimento" value={value?.development} onChange={(v) => update('development', v)} rows={3} />
      </div>
    </div>
  );
}

function MainActivityBlock({ title, value, onChange, showTime = false }) {
  const update = (field, fieldValue) => onChange({ ...(value || {}), [field]: fieldValue });
  return (
    <div className="ped-eval__subcard">
      <h5>{title}</h5>
      <div className="ped-eval__grid">
        {showTime && <TextInput label="Horário" value={value?.time} onChange={(v) => update('time', v)} />}
        <TextInput label="Tema" value={value?.theme} onChange={(v) => update('theme', v)} />
        <TextInput label="Espaço" value={value?.space} onChange={(v) => update('space', v)} />
        <TextInput label="Área de conhecimento" value={value?.knowledgeArea} onChange={(v) => update('knowledgeArea', v)} />
        <TextInput label="Eixo(s)" value={value?.axis} onChange={(v) => update('axis', v)} />
        <TextInput label="Metas" value={value?.goals} onChange={(v) => update('goals', v)} />
        <TextInput label="Objetivos" value={value?.objectives} onChange={(v) => update('objectives', v)} />
        <TextInput label="Conteúdos" value={value?.contents} onChange={(v) => update('contents', v)} wide />
        <TextArea label="Desenvolvimento" value={value?.development} onChange={(v) => update('development', v)} rows={4} />
        <TextArea label="Recursos" value={value?.resources} onChange={(v) => update('resources', v)} rows={2} />
      </div>
    </div>
  );
}

function PlanningForm({ data, setData, responsibleName }) {
  const [activeDay, setActiveDay] = useState(WEEK_DAYS[0][0]);
  const update = (field, value) => setData((current) => ({ ...current, [field]: value }));
  const updateDay = (dayKey, patch) =>
    setData((current) => ({
      ...current,
      days: {
        ...current.days,
        [dayKey]: { ...(current.days?.[dayKey] || {}), ...patch },
      },
    }));
  const updateWeekly = (dayKey, value) =>
    setData((current) => ({
      ...current,
      weekly: { ...current.weekly, [dayKey]: { proposal: value } },
    }));

  const [activeDayKey, activeDayLabel] = WEEK_DAYS.find(([key]) => key === activeDay) || WEEK_DAYS[0];
  const day = data.days?.[activeDayKey] || {};
  const dayProposal = data.weekly?.[activeDayKey]?.proposal || '';

  const dayProgress = (dayKey) => {
    const current = data.days?.[dayKey] || {};
    const checks = [
      Boolean(current.date),
      Boolean(data.weekly?.[dayKey]?.proposal?.trim()),
      Boolean(current.mainActivity?.theme?.trim() || current.mainActivity?.development?.trim()),
      Boolean(current.observationEvaluation?.trim()),
    ];
    return checks.filter(Boolean).length;
  };

  return (
    <div className="ped-eval__form-stack">
      <section className="ped-eval__card ped-eval__card--guided">
        <div className="ped-eval__guided-head">
          <span className="ped-eval__step-number">1</span>
          <div>
            <h4>Dados da semana</h4>
            <p>Defina o período e o responsável. Esses dados serão reutilizados no documento.</p>
          </div>
        </div>
        <div className="ped-eval__grid">
          <TextInput label="Mês" value={data.month} onChange={(v) => update('month', v)} />
          <TextInput label="Ano" type="number" value={data.year} onChange={(v) => update('year', v)} />
          <TextInput label="Semana de" type="date" value={data.weekStart} onChange={(v) => update('weekStart', v)} />
          <TextInput label="Até" type="date" value={data.weekEnd} onChange={(v) => update('weekEnd', v)} />
          <TextInput label="Professor(a)" value={data.teacherName || responsibleName} onChange={(v) => update('teacherName', v)} wide />
          <TextArea label="Resumo / bilhete pedagógico" value={data.pedagogicalSummary} onChange={(v) => update('pedagogicalSummary', v)} rows={3} />
        </div>
      </section>

      <section className="ped-eval__card ped-eval__card--guided">
        <div className="ped-eval__guided-head">
          <span className="ped-eval__step-number">2</span>
          <div>
            <h4>Eixos trabalhados</h4>
            <p>Marque somente os eixos que realmente serão trabalhados nesta semana.</p>
          </div>
        </div>
        <div className="ped-eval__checks">
          {PEDAGOGICAL_AXES.map((axis) => (
            <label key={axis} className={data.axes?.[axis] ? 'selected' : ''}>
              <input
                type="checkbox"
                checked={Boolean(data.axes?.[axis])}
                onChange={(event) => setData((current) => ({
                  ...current,
                  axes: { ...current.axes, [axis]: event.target.checked },
                }))}
              />
              <span>{axis}</span>
            </label>
          ))}
        </div>
      </section>

      <section className="ped-eval__card ped-eval__card--guided">
        <div className="ped-eval__guided-head">
          <span className="ped-eval__step-number">3</span>
          <div>
            <h4>Planejamento por dia</h4>
            <p>Trabalhe um dia por vez. Os demais ficam salvos sem ocupar a tela inteira.</p>
          </div>
        </div>

        <nav className="ped-eval__day-tabs" aria-label="Dias do planejamento">
          {WEEK_DAYS.map(([key, label]) => {
            const progress = dayProgress(key);
            const date = data.days?.[key]?.date;
            return (
              <button
                type="button"
                key={key}
                className={activeDayKey === key ? 'active' : ''}
                onClick={() => setActiveDay(key)}
              >
                <span>{label.replace('-feira', '')}</span>
                <small>{date ? String(date).split('-').reverse().slice(0, 2).join('/') : `${progress}/4`}</small>
                <i style={{ width: `${progress * 25}%` }} />
              </button>
            );
          })}
        </nav>

        <div className="ped-eval__day-workspace">
          <div className="ped-eval__day-workspace-head">
            <div>
              <span>Dia selecionado</span>
              <strong>{activeDayLabel}</strong>
            </div>
            <span className="ped-eval__progress-pill">{dayProgress(activeDayKey)}/4 etapas preenchidas</span>
          </div>

          <div className="ped-eval__grid">
            <TextInput label="Data" type="date" value={day.date} onChange={(v) => updateDay(activeDayKey, { date: v })} />
            <TextArea label="Proposta principal do dia" value={dayProposal} onChange={(v) => updateWeekly(activeDayKey, v)} rows={3} />
          </div>

          <div className="ped-eval__subsection-title">
            <strong>Rotina</strong>
            <span>Horários básicos do dia</span>
          </div>
          <div className="ped-eval__routine-grid">
            <TextInput label="Acolhimento" value={day.arrivalTime} onChange={(v) => updateDay(activeDayKey, { arrivalTime: v })} />
            <TextInput label="Café da manhã" value={day.breakfastTime} onChange={(v) => updateDay(activeDayKey, { breakfastTime: v })} />
            <TextInput label="Higiene" value={day.hygieneTime} onChange={(v) => updateDay(activeDayKey, { hygieneTime: v })} />
            <TextInput label="Almoço / jantar" value={day.lunchTime} onChange={(v) => updateDay(activeDayKey, { lunchTime: v })} />
            <TextInput label="Água / banheiro" value={day.waterBathroomTime} onChange={(v) => updateDay(activeDayKey, { waterBathroomTime: v })} />
            <TextInput label="Soninho / saída" value={day.sleepTime} onChange={(v) => updateDay(activeDayKey, { sleepTime: v })} />
          </div>

          <div className="ped-eval__subsection-title">
            <strong>Momentos pedagógicos</strong>
            <span>Preencha conforme a rotina da turma</span>
          </div>
          <div className="ped-eval__activity-grid">
            <PlanningActivityBlock title="Chamada" value={day.call} onChange={(v) => updateDay(activeDayKey, { call: v })} />
            <PlanningActivityBlock title="Roda de conversa / músicas" value={day.conversationMusic} onChange={(v) => updateDay(activeDayKey, { conversationMusic: v })} />
            <PlanningActivityBlock title="Calendário" value={day.calendar} onChange={(v) => updateDay(activeDayKey, { calendar: v })} />
            <PlanningActivityBlock title="Ajudante do dia" value={day.helperOfDay} onChange={(v) => updateDay(activeDayKey, { helperOfDay: v })} />
          </div>

          <div className="ped-eval__subsection-title">
            <strong>Atividades</strong>
            <span>Descreva objetivo, desenvolvimento e recursos</span>
          </div>
          <MainActivityBlock title="Atividade principal" value={day.mainActivity} onChange={(v) => updateDay(activeDayKey, { mainActivity: v })} />
          <MainActivityBlock title="Atividade secundária" value={day.secondaryActivity} showTime onChange={(v) => updateDay(activeDayKey, { secondaryActivity: v })} />

          <div className="ped-eval__subsection-title">
            <strong>Fechamento do dia</strong>
            <span>Registre a observação e a avaliação</span>
          </div>
          <TextArea label="Observação / Avaliação" value={day.observationEvaluation} onChange={(v) => updateDay(activeDayKey, { observationEvaluation: v })} rows={5} />

          <div className="ped-eval__day-next">
            {WEEK_DAYS.findIndex(([key]) => key === activeDayKey) > 0 && (
              <button type="button" onClick={() => setActiveDay(WEEK_DAYS[WEEK_DAYS.findIndex(([key]) => key === activeDayKey) - 1][0])}>← Dia anterior</button>
            )}
            {WEEK_DAYS.findIndex(([key]) => key === activeDayKey) < WEEK_DAYS.length - 1 && (
              <button type="button" className="primary" onClick={() => setActiveDay(WEEK_DAYS[WEEK_DAYS.findIndex(([key]) => key === activeDayKey) + 1][0])}>Próximo dia →</button>
            )}
          </div>
        </div>
      </section>
    </div>
  );
}

function ReferralForm({ data, setData, responsibleName }) {
  const update = (field, value) => setData((current) => ({ ...current, [field]: value }));
  return (
    <div className="ped-eval__form-stack">
      <section className="ped-eval__card ped-eval__card--guided">
        <div className="ped-eval__guided-head"><span className="ped-eval__step-number">1</span><div><h4>Identificação</h4><p>Aluno e turma vêm do cadastro; informe apenas data e professor.</p></div></div>
        <div className="ped-eval__grid">
          <TextInput label="Data" type="date" value={data.date} onChange={(v) => update('date', v)} />
          <TextInput label="Professor da classe comum" value={data.teacherName || responsibleName} onChange={(v) => update('teacherName', v)} />
        </div>
      </section>

      <section className="ped-eval__card ped-eval__card--guided">
        <div className="ped-eval__guided-head"><span className="ped-eval__step-number">2</span><div><h4>Critérios de elegibilidade</h4><p>Marque somente as condições aplicáveis ao encaminhamento.</p></div></div>
        <div className="ped-eval__checks ped-eval__checks--wide">
          {ELIGIBILITY_OPTIONS.map(([key, label]) => (
            <label key={key}>
              <input
                type="checkbox"
                checked={Boolean(data.eligibility?.[key])}
                onChange={(event) => setData((current) => ({
                  ...current,
                  eligibility: { ...current.eligibility, [key]: event.target.checked },
                }))}
              />
              <span>{label}</span>
            </label>
          ))}
        </div>
        <TextInput label="Outros" value={data.otherEligibility} onChange={(v) => update('otherEligibility', v)} wide />
      </section>

      <section className="ped-eval__card ped-eval__card--guided">
        <div className="ped-eval__guided-head"><span className="ped-eval__step-number">3</span><div><h4>Relato pedagógico</h4><p>Registre o histórico, o motivo e as providências já adotadas.</p></div></div>
        <TextArea label="Breve histórico do estudante (síntese dos dados coletados com a família)" value={data.studentHistory} onChange={(v) => update('studentHistory', v)} rows={7} />
        <TextArea label="Motivo do encaminhamento (síntese da avaliação do professor da classe comum)" value={data.referralReason} onChange={(v) => update('referralReason', v)} rows={7} />
        <TextArea label="Providências já tomadas para atender às necessidades educacionais apresentadas pelo estudante" value={data.measuresTaken} onChange={(v) => update('measuresTaken', v)} rows={7} />
      </section>

      <section className="ped-eval__card ped-eval__card--guided">
        <div className="ped-eval__guided-head"><span className="ped-eval__step-number">4</span><div><h4>Assinaturas</h4><p>Informe os nomes que devem aparecer no documento final.</p></div></div>
        <div className="ped-eval__grid">
          <TextInput label="Pai ou responsável legal" value={data.signatures?.guardian} onChange={(v) => setData((current) => ({ ...current, signatures: { ...current.signatures, guardian: v } }))} />
          <TextInput label="Diretor da Unidade Escolar" value={data.signatures?.director} onChange={(v) => setData((current) => ({ ...current, signatures: { ...current.signatures, director: v } }))} />
          <TextInput label="Professor responsável pelo encaminhamento" value={data.signatures?.teacher || data.teacherName || responsibleName} onChange={(v) => setData((current) => ({ ...current, signatures: { ...current.signatures, teacher: v } }))} wide />
        </div>
      </section>
    </div>
  );
}

function EvaluationItem({ sectionKey, item, value, onChange }) {
  const current = value || { status: '', help: '', choices: [], extra: '' };
  const update = (patch) => onChange({ ...current, ...patch });
  const selectedChoices = Array.isArray(current.choices) ? current.choices : [];
  return (
    <div className="ped-eval__evaluation-row">
      <div className="ped-eval__evaluation-question">
        <strong>{item.label}</strong>
        {item.choices?.length ? (
          <div className="ped-eval__inline-checks">
            {item.choices.map((choice) => (
              <label key={choice}>
                <input
                  type="checkbox"
                  checked={selectedChoices.includes(choice)}
                  onChange={(event) => update({
                    choices: event.target.checked
                      ? [...selectedChoices, choice]
                      : selectedChoices.filter((entry) => entry !== choice),
                  })}
                />
                <span>{choice}</span>
              </label>
            ))}
          </div>
        ) : null}
        {item.extraLabel ? (
          <input
            className="ped-eval__compact-input"
            placeholder={item.extraLabel}
            value={current.extra || ''}
            onChange={(event) => update({ extra: event.target.value })}
          />
        ) : null}
      </div>
      <select
        aria-label={`Avaliação - ${item.label}`}
        value={current.status || ''}
        onChange={(event) => update({ status: event.target.value })}
      >
        <option value="">—</option>
        {EVALUATION_STATUS_OPTIONS.map(([key, label]) => (
          <option key={key} value={key}>{key} · {label}</option>
        ))}
      </select>
      <textarea
        aria-label={`Tipos de ajuda - ${item.label}`}
        rows={2}
        placeholder="Tipos de ajuda / apoio utilizado"
        value={current.help || ''}
        onChange={(event) => update({ help: event.target.value })}
      />
    </div>
  );
}

function EvaluationForm({ data, setData, responsibleName }) {
  const [activeSectionKey, setActiveSectionKey] = useState(PEDAGOGICAL_EVALUATION_SECTIONS[0]?.key || '');
  const update = (field, value) => setData((current) => ({ ...current, [field]: value }));
  const setItem = (sectionKey, itemKey, value) => setData((current) => ({
    ...current,
    sections: {
      ...current.sections,
      [sectionKey]: {
        ...current.sections?.[sectionKey],
        items: {
          ...(current.sections?.[sectionKey]?.items || {}),
          [itemKey]: value,
        },
      },
    },
  }));

  const activeSection = PEDAGOGICAL_EVALUATION_SECTIONS.find((section) => section.key === activeSectionKey)
    || PEDAGOGICAL_EVALUATION_SECTIONS[0];

  const sectionProgress = (section) => {
    const entries = section.groups.flatMap((group) => group.items);
    const completed = entries.filter((item) => Boolean(data.sections?.[section.key]?.items?.[item.key]?.status)).length;
    return { completed, total: entries.length };
  };

  const activeIndex = PEDAGOGICAL_EVALUATION_SECTIONS.findIndex((section) => section.key === activeSection?.key);
  const activeProgress = sectionProgress(activeSection);

  return (
    <div className="ped-eval__form-stack">
      <section className="ped-eval__card ped-eval__card--guided">
        <div className="ped-eval__guided-head">
          <span className="ped-eval__step-number">1</span>
          <div>
            <h4>Identificação</h4>
            <p>Aluno e turma já vêm do cadastro. Informe as datas e o professor responsável.</p>
          </div>
        </div>
        <div className="ped-eval__grid">
          <TextInput label="Data" type="date" value={data.date} onChange={(v) => update('date', v)} />
          <TextInput label="Data do encaminhamento" type="date" value={data.referralDate} onChange={(v) => update('referralDate', v)} />
          <TextInput label="Professor de Classe Comum" value={data.teacherName || responsibleName} onChange={(v) => update('teacherName', v)} wide />
        </div>
      </section>

      <section className="ped-eval__card ped-eval__card--guided">
        <div className="ped-eval__guided-head">
          <span className="ped-eval__step-number">2</span>
          <div>
            <h4>Avaliação por área</h4>
            <p>Escolha uma área e avalie os itens sem precisar rolar o formulário inteiro.</p>
          </div>
        </div>

        <div className="ped-eval__legend ped-eval__legend--compact">
          {EVALUATION_STATUS_OPTIONS.map(([key, label]) => <span key={key}><b>{key}</b>{label}</span>)}
        </div>

        <nav className="ped-eval__domain-tabs" aria-label="Áreas da avaliação pedagógica">
          {PEDAGOGICAL_EVALUATION_SECTIONS.map((section) => {
            const progress = sectionProgress(section);
            return (
              <button
                type="button"
                key={section.key}
                className={activeSection?.key === section.key ? 'active' : ''}
                onClick={() => setActiveSectionKey(section.key)}
              >
                <span>{section.title}</span>
                <small>{progress.completed}/{progress.total}</small>
                <i style={{ width: `${progress.total ? (progress.completed / progress.total) * 100 : 0}%` }} />
              </button>
            );
          })}
        </nav>

        {activeSection && (
          <div className="ped-eval__domain-workspace">
            <div className="ped-eval__domain-head">
              <div>
                <span>Área selecionada</span>
                <strong>{activeSection.title}</strong>
              </div>
              <span className="ped-eval__progress-pill">{activeProgress.completed}/{activeProgress.total} avaliados</span>
            </div>

            <div className="ped-eval__evaluation-head">
              <span>Item avaliado</span><span>Avaliação</span><span>Tipos de ajuda</span>
            </div>

            {activeSection.groups.map((group) => (
              <div key={group.title} className="ped-eval__evaluation-group">
                <h5>{group.title}</h5>
                {group.items.map((item) => (
                  <EvaluationItem
                    key={item.key}
                    sectionKey={activeSection.key}
                    item={item}
                    value={data.sections?.[activeSection.key]?.items?.[item.key]}
                    onChange={(value) => setItem(activeSection.key, item.key, value)}
                  />
                ))}
              </div>
            ))}

            <div className="ped-eval__observations">
              <TextArea
                label={`Observações - ${activeSection.title}`}
                value={data.sections?.[activeSection.key]?.observations}
                onChange={(value) => setData((current) => ({
                  ...current,
                  sections: {
                    ...current.sections,
                    [activeSection.key]: {
                      ...current.sections?.[activeSection.key],
                      observations: value,
                    },
                  },
                }))}
                rows={5}
              />
            </div>

            <div className="ped-eval__day-next">
              {activeIndex > 0 && (
                <button type="button" onClick={() => setActiveSectionKey(PEDAGOGICAL_EVALUATION_SECTIONS[activeIndex - 1].key)}>← Área anterior</button>
              )}
              {activeIndex < PEDAGOGICAL_EVALUATION_SECTIONS.length - 1 && (
                <button type="button" className="primary" onClick={() => setActiveSectionKey(PEDAGOGICAL_EVALUATION_SECTIONS[activeIndex + 1].key)}>Próxima área →</button>
              )}
            </div>
          </div>
        )}
      </section>

      <section className="ped-eval__card ped-eval__card--guided">
        <div className="ped-eval__guided-head">
          <span className="ped-eval__step-number">3</span>
          <div>
            <h4>Assinaturas</h4>
            <p>Esses nomes serão posicionados no PDF final.</p>
          </div>
        </div>
        <div className="ped-eval__grid">
          <TextInput label="Diretor da Unidade Escolar" value={data.signatures?.director} onChange={(v) => setData((current) => ({ ...current, signatures: { ...current.signatures, director: v } }))} />
          <TextInput label="Professor de Classe Comum" value={data.signatures?.teacher || data.teacherName || responsibleName} onChange={(v) => setData((current) => ({ ...current, signatures: { ...current.signatures, teacher: v } }))} />
        </div>
      </section>
    </div>
  );
}

export default function PedagogicalEvaluationPanel({
  students = [],
  classes = [],
  canManage = false,
  responsibleName = '',
}) {
  const [type, setType] = useState('PLANNING');
  const [studentId, setStudentId] = useState('');
  const [classId, setClassId] = useState('');
  const [data, setData] = useState(() => createPlanningData(responsibleName));
  const [recordId, setRecordId] = useState(null);
  const [records, setRecords] = useState([]);
  const [loadingRecords, setLoadingRecords] = useState(false);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');

  const activeStudents = useMemo(
    () => students.filter((student) => String(student.status || '').toUpperCase() === 'ACTIVE'),
    [students],
  );
  const selectedStudent = useMemo(
    () => students.find((student) => String(student.id) === String(studentId)) || null,
    [students, studentId],
  );
  const effectiveClassId = type === 'PLANNING' ? classId : (selectedStudent?.classId || classId);
  const selectedClass = useMemo(
    () => classes.find((klass) => String(klass.id) === String(effectiveClassId)) || null,
    [classes, effectiveClassId],
  );
  const currentType = TYPE_OPTIONS.find((item) => item.key === type) || TYPE_OPTIONS[0];

  const targetReady = type === 'PLANNING' ? Boolean(classId) : Boolean(studentId);

  const resetDocument = (nextType = type) => {
    setRecordId(null);
    setData(createData(nextType, responsibleName));
    setMessage('');
    setError('');
  };

  useEffect(() => {
    resetDocument(type);
    setRecords([]);
    if (type === 'PLANNING') setStudentId('');
  }, [type]);

  useEffect(() => {
    if (selectedStudent?.classId && type !== 'PLANNING') setClassId(String(selectedStudent.classId));
  }, [selectedStudent?.classId, type]);

  useEffect(() => {
    let active = true;
    const load = async () => {
      if (!targetReady) {
        setRecords([]);
        return;
      }
      setLoadingRecords(true);
      setError('');
      try {
        const params = type === 'PLANNING'
          ? { type, classId }
          : { type, studentId };
        const list = await StudentManagementService.listPedagogicalRecords(params);
        if (!active) return;
        const normalized = Array.isArray(list) ? list : [];
        setRecords(normalized);
        if (normalized.length) {
          const latest = normalized[0];
          setRecordId(latest.id);
          setData(normalizeLoadedData(type, latest.data, responsibleName));
        } else {
          setRecordId(null);
          setData(createData(type, responsibleName));
        }
      } catch (err) {
        if (active) setError(err?.response?.data?.error || 'Não foi possível carregar os registros pedagógicos.');
      } finally {
        if (active) setLoadingRecords(false);
      }
    };
    load();
    return () => { active = false; };
  }, [type, studentId, classId, targetReady, responsibleName]);

  const chooseRecord = (id) => {
    const record = records.find((item) => String(item.id) === String(id));
    if (!record) {
      resetDocument(type);
      return;
    }
    setRecordId(record.id);
    setData(normalizeLoadedData(type, record.data, responsibleName));
    setMessage('');
    setError('');
  };

  const save = async () => {
    if (!targetReady) {
      setError(type === 'PLANNING' ? 'Selecione uma turma.' : 'Selecione um aluno.');
      return;
    }
    setSaving(true);
    setError('');
    setMessage('');
    try {
      const saved = await StudentManagementService.savePedagogicalRecord({
        id: recordId || undefined,
        type,
        studentId: type === 'PLANNING' ? null : Number(studentId),
        classId: effectiveClassId ? Number(effectiveClassId) : null,
        referenceDate: data.date || data.weekStart || new Date().toISOString().slice(0, 10),
        data,
      });
      setRecordId(saved.id);
      setRecords((current) => [saved, ...current.filter((item) => item.id !== saved.id)]);
      setMessage('Documento pedagógico salvo com sucesso.');
    } catch (err) {
      setError(err?.response?.data?.error || 'Não foi possível salvar o documento pedagógico.');
    } finally {
      setSaving(false);
    }
  };

  const remove = async () => {
    if (!recordId || !canManage) return;
    if (!window.confirm('Deseja excluir este registro pedagógico?')) return;
    setSaving(true);
    setError('');
    try {
      await StudentManagementService.deletePedagogicalRecord(recordId);
      const nextRecords = records.filter((item) => item.id !== recordId);
      setRecords(nextRecords);
      if (nextRecords.length) {
        setRecordId(nextRecords[0].id);
        setData(normalizeLoadedData(type, nextRecords[0].data, responsibleName));
      } else {
        resetDocument(type);
      }
      setMessage('Registro pedagógico excluído.');
    } catch (err) {
      setError(err?.response?.data?.error || 'Não foi possível excluir o registro.');
    } finally {
      setSaving(false);
    }
  };

  const generatePdf = async () => {
    if (!targetReady) {
      setError(type === 'PLANNING' ? 'Selecione uma turma antes de gerar o PDF.' : 'Selecione um aluno antes de gerar o PDF.');
      return;
    }
    setSaving(true);
    setError('');
    try {
      await downloadStudentPedagogicalPdf({
        type,
        data,
        student: selectedStudent,
        klass: selectedClass,
        responsibleName,
      });
    } catch (err) {
      setError(err?.message || 'Não foi possível gerar o PDF.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <section className="ped-eval">
      <header className="ped-eval__hero">
        <div>
          <span className="ped-eval__hero-icon"><FaGraduationCap /></span>
          <div>
            <h2>Avaliação Pedagógica</h2>
            <p>Preencha, salve e gere os documentos pedagógicos diretamente pelo Diário de Classe.</p>
          </div>
        </div>
        <span className="ped-eval__hero-badge">Gestão de Alunos</span>
      </header>

      {error && <div className="ped-eval__alert ped-eval__alert--error">{error}</div>}
      {message && <div className="ped-eval__alert"><FaCheckCircle /> {message}</div>}

      <nav className="ped-eval__type-tabs">
        {TYPE_OPTIONS.map((item) => {
          const Icon = item.icon;
          return (
            <button
              type="button"
              key={item.key}
              className={type === item.key ? 'active' : ''}
              onClick={() => setType(item.key)}
            >
              <Icon />
              <span>{item.shortLabel}</span>
            </button>
          );
        })}
      </nav>

      <div className="ped-eval__intro">
        <strong>{currentType.label}</strong>
        <span>{currentType.description}</span>
      </div>

      <section className="ped-eval__selector">
        {type === 'PLANNING' ? (
          <SelectField
            label="Turma"
            value={classId}
            onChange={(value) => { setClassId(value); setRecordId(null); }}
            options={classes.map((klass) => ({ value: klass.id, label: `${klass.name}${klass.period ? ` · ${klass.period}` : ''}` }))}
          />
        ) : (
          <SelectField
            label="Aluno"
            value={studentId}
            onChange={(value) => { setStudentId(value); setRecordId(null); }}
            options={activeStudents.map((student) => ({ value: student.id, label: `${student.fullName} · ${student.registrationNumber || 'Sem matrícula'}` }))}
            wide
          />
        )}

        {records.length > 0 && (
          <SelectField
            label="Registro salvo"
            value={recordId || ''}
            onChange={chooseRecord}
            options={records.map((record) => ({
              value: record.id,
              label: `#${record.id} · ${formatDateTime(record.updatedAt || record.createdAt)}`,
            }))}
          />
        )}

        <div className="ped-eval__selector-actions">
          <button type="button" onClick={() => resetDocument(type)} disabled={saving}>
            Novo registro
          </button>
        </div>
      </section>

      {type !== 'PLANNING' && <IdentityCard student={selectedStudent} klass={selectedClass} />}

      {!targetReady ? (
        <div className="ped-eval__empty">
          <FaGraduationCap />
          <strong>{type === 'PLANNING' ? 'Selecione uma turma' : 'Selecione um aluno'}</strong>
          <span>Os dados cadastrais serão puxados automaticamente do sistema.</span>
        </div>
      ) : loadingRecords ? (
        <div className="ped-eval__empty"><span>Carregando registros...</span></div>
      ) : (
        <>
          {type === 'PLANNING' && <PlanningForm data={data} setData={setData} responsibleName={responsibleName} />}
          {type === 'SPECIAL_EDUCATION_REFERRAL' && <ReferralForm data={data} setData={setData} responsibleName={responsibleName} />}
          {type === 'PEDAGOGICAL_EVALUATION' && <EvaluationForm data={data} setData={setData} responsibleName={responsibleName} />}

          <div className="ped-eval__sticky-actions">
            <div>
              {recordId ? <span><FaHistory /> Registro #{recordId}</span> : <span>Novo documento</span>}
            </div>
            <div>
              {recordId && canManage && (
                <button type="button" className="danger" onClick={remove} disabled={saving}>
                  <FaTrash /> Excluir
                </button>
              )}
              <button type="button" className="secondary" onClick={generatePdf} disabled={saving}>
                <FaDownload /> Gerar PDF
              </button>
              {canManage && (
                <button type="button" className="primary" onClick={save} disabled={saving}>
                  <FaSave /> {saving ? 'Salvando...' : 'Salvar'}
                </button>
              )}
            </div>
          </div>
        </>
      )}
    </section>
  );
}
